import { useMemo, useCallback } from "react";
import { motion, useIsPresent } from "framer-motion";
import { useParams, useLocation } from 'react-router-dom';
import Loader from '../components/Loader';

import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Card from '../components/Card';
import PageTemplate from '../components/PageTemplate';
import HomeCardSection from '../components/HomeCardSection';
import HeaderImage from '../components/HeaderImage';

import { useFirestoreData } from '../../context';
import { filterByProperty } from "../../utils";

export function SearchAndFilter() {
  const { state } = useLocation();
  const isPresent = useIsPresent();
  const { slug } = useParams();
  const { providers, loading } = useFirestoreData();

  const filteredProviders = useMemo(() => {
    if (!slug || loading) return [];
    // Adapt structure for filterByProperty utility
    const mappedProviders = providers.map(p => ({
      ...p,
      name: p.name // Map firestore "name" back to "name" for UI compatibility
    }));
    return filterByProperty(mappedProviders, "type", "National Parks & Gardens");
  }, [slug, providers, loading]);

  const renderCards = useCallback(() => {
    return filteredProviders.map(p => (
      <Card key={p.id} provider={p} />
    ));
  }, [filteredProviders]);

  if (loading) return <Loader />;

  // Graceful empty state instead of a blank `return null` when there's no
  // navigation state (e.g. /main opened directly).
  if (!state) {
    return (
      <PageTemplate>
        <Toolbar />
        <Box sx={{ px: { xs: 2, sm: 6 }, py: 8, textAlign: 'center' }}>
          <Typography variant="h5" component="h1" sx={{ mb: 1 }}>
            Nothing to show here yet
          </Typography>
          <Typography color="text.secondary">
            Pick a destination from the home page to explore places to stay and visit.
          </Typography>
        </Box>
      </PageTemplate>
    );
  }

  const hasResults = filteredProviders.length > 0;

  return (
    <PageTemplate>
      <Toolbar />
      <Box>
        <HeaderImage image={state.url} />
      </Box>
      <Box sx={{ px: { xs: 2, sm: 6 }, py: { xs: 3, sm: 5 } }}>
        <motion.div
          initial="initial"
          animate="enter"
          exit="exit"
          variants={{ exit: { transition: { staggerChildren: 0.1 } } }}
        >
          <HomeCardSection title={state.title} />
        </motion.div>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 6 }, pb: { xs: 4, sm: 6 } }}>
        <HomeCardSection title='' />
        {hasResults ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, mt: 2.5 }}>
            {renderCards()}
          </Box>
        ) : (
          <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
            No places match this search yet.
          </Typography>
        )}
      </Box>

      <motion.div
        initial={{ scaleX: 1 }}
        animate={{ scaleX: 0, transition: { duration: 0.5, ease: "circOut" } }}
        exit={{ scaleX: 1, transition: { duration: 0.5, ease: "circIn" } }}
        style={{ originX: isPresent ? 0 : 1, zIndex: 1 }}
        className="privacy-screen"
      />
    </PageTemplate>
  );
}
